/**
 * Legacy / Voxcanon meeting-detail layout (and the shared leftover path
 * Lexicanon falls through to when the summary/finalize/live surfaces don't
 * apply). JSX moved as-is from the route — including TTRPG panels.
 */
import type {
  IDisplaySection,
  IProgressiveSummary,
  ISpeakerHypothesisSnapshot,
  IStreamingSegment,
} from "@lexicanon/shared"
import { isLiteTemplate, MEETING_STATUSES } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Anchor, Badge, Box, Button, Container, Group, Paper, ScrollArea, Stack, Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import type { QueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { useState } from "react"
import { CanonReviewPane } from "../../../../components/CanonReviewPane"
import { EditableField } from "../../../../components/EditableField"
import { ExportMenu } from "../../../../components/ExportMenu"
import { FinalizationProgress } from "../../../../components/FinalizationProgress"
import { MeetingAccessModal } from "../../../../components/MeetingAccessModal"
import { meetingErrorReason } from "../../../../components/MeetingCard/helpers"
import { MeetingLanguageSwitcher } from "../../../../components/MeetingLanguageSwitcher"
import { MeetingStats } from "../../../../components/MeetingStats"
import { Player } from "../../../../components/Player"
import type { IAudioPlayerRef } from "../../../../components/Player/interfaces"
import { RecordingControls } from "../../../../components/RecordingControls"
import { SpeakerHypothesisStrip } from "../../../../components/RecordingSurface/SpeakerHypothesisStrip"
import { ReprocessForm } from "../../../../components/ReprocessForm"
import { ShareMeetingMenu } from "../../../../components/ShareMeetingMenu"
import { SpeakerPanel } from "../../../../components/SpeakerPanel"
import { StatusPill } from "../../../../components/StatusPill"
import { type IEditContext, TemplateAnalysisView } from "../../../../components/TemplateAnalysisView"
import { TTRPGPanel } from "../../../../components/TTRPGPanel"
import { TTRPGReports } from "../../../../components/TTRPGReports"
import { SpeakerColorsProvider } from "../../../../contexts/SpeakerColors"
import type { CompanyApi } from "../../../../hooks/useCompanyApi"
import {
  deleteMeetingPermanently,
  hideMeeting,
  reprocessMeeting,
  unhideMeeting,
  updateSubject,
} from "../../../../lib/api"
import { formatDateTime } from "../../../../lib/format-date"
import { displayMeetingTitle } from "../../../../lib/meeting-title"
import { notifyError } from "../../../../lib/notify"
import type { TProcessingStage } from "../../../../lib/pipeline-progress"
import { queryKeys } from "../../../../lib/query-keys"
import { normalizeAnalysis, speakerLabelDisplayMap } from "../../../../lib/speaker-mapping"
import type { ISpeakerVMResult } from "../../../../lib/speaker-vm"
import type { IMeetingDetail } from "../../../../lib/types/meeting"
import styles from "../meeting-detail.module.css"
import { FailedMeetingView } from "./FailedMeetingView"
import { useMeetingLifecycleActions } from "./useMeetingLifecycleActions"
import type { IAvailableTranslation } from "./useMeetingTranslation"

export interface IObserverLiveData {
  isStreaming: boolean
  speakerHypotheses: Map<string, ISpeakerHypothesisSnapshot>
  segments: IStreamingSegment[]
  interimText: string
  interimSpeaker: string
  latestSummary: IProgressiveSummary | null
}

export interface IMeetingDetailLegacyProps {
  meeting: IMeetingDetail & { attendee_emails?: string }
  meetingId: string
  orgId: string | null
  company: CompanyApi
  companyId: string | undefined
  queryClient: QueryClient
  speakerVMs: ISpeakerVMResult
  speakerColorMap: Map<string, string>
  displayAnalysis: Record<string, unknown> | null
  accessOpened: boolean
  closeAccess: () => void
  openAccess: () => void
  canManageAccess: boolean
  isOrgAdmin: boolean
  isOrganizer: boolean
  isActivelyRecording: boolean
  isObserver: boolean
  isIntermediate: boolean
  /** Emails the meeting was deliberately shared with (organizer excluded). */
  sharedWithEmails: string[]
  seekSeconds?: number
  sourceLocale: string
  analysisLocale: string
  availableTranslations: IAvailableTranslation[]
  isTranslating: boolean
  hasTranslationError: boolean
  onLocaleChange: (locale: string) => void
  liveData: IObserverLiveData
  hypothesisPending: Set<string>
  onHypothesisAction: (label: string, name: string, action: "confirm" | "reject") => void
  audioPlayerRef: React.RefObject<IAudioPlayerRef | null>
  effectiveStage: TProcessingStage | null
  effectiveProgress: number
  currentStepName: string | null
  myRole: string | null
  refetch: () => unknown
  navigateHome: () => void
}

export function MeetingDetailLegacy({
  meeting,
  meetingId,
  orgId,
  company,
  companyId,
  queryClient,
  speakerVMs,
  speakerColorMap,
  displayAnalysis,
  accessOpened,
  closeAccess,
  openAccess,
  canManageAccess,
  isOrgAdmin,
  isOrganizer,
  isActivelyRecording,
  isObserver,
  isIntermediate,
  sharedWithEmails,
  seekSeconds,
  sourceLocale,
  analysisLocale,
  availableTranslations,
  isTranslating,
  hasTranslationError,
  onLocaleChange,
  liveData,
  hypothesisPending,
  onHypothesisAction,
  audioPlayerRef,
  effectiveStage,
  effectiveProgress,
  currentStepName,
  myRole,
  refetch,
  navigateHome,
}: IMeetingDetailLegacyProps): React.JSX.Element {
  const { i18n } = useLingui()
  const id = meetingId
  const [retrying, setRetrying] = useState(false)
  const [showAudio, setShowAudio] = useState(false)
  const lifecycle = useMeetingLifecycleActions({
    company,
    meetingId: id,
    orgId,
    queryClient,
    refetch,
    navigateHome,
    isOrgAdmin,
    isOrganizer,
    hidden: meeting.hidden ?? false,
  })

  const handleRetry = (): void => {
    if (retrying) return
    setRetrying(true)
    void reprocessMeeting(company, id)
      .then(() => {
        void refetch()
      })
      .catch((err: unknown) => {
        notifyError(err, i18n._(msg`Could not try again`))
      })
      .finally(() => {
        setRetrying(false)
      })
  }

  if (meeting.status === MEETING_STATUSES.ERROR && !isActivelyRecording) {
    return (
      <SpeakerColorsProvider colors={speakerColorMap}>
        <FailedMeetingView
          title={displayMeetingTitle(meeting.subject, meeting.topics, i18n, {
            lite: isLiteTemplate(meeting.template_id),
            startedAt: meeting.start_time,
          })}
          dateLabel={meeting.start_time ? formatDateTime(i18n, meeting.start_time) : ""}
          reason={meetingErrorReason({ error: meeting.error, processing_stage: meeting.processing_stage }, i18n)}
          canRetry={isOrgAdmin || isOrganizer}
          canDelete={lifecycle.onDelete !== undefined}
          permanent={isOrgAdmin}
          retrying={retrying}
          hasRecording={Boolean(meeting.recording_url)}
          onRetry={handleRetry}
          onDelete={() => {
            lifecycle.onDelete?.()
          }}
          onPlay={() => {
            setShowAudio(true)
          }}
          audio={
            showAudio && meeting.recording_url ? (
              <Player
                ref={audioPlayerRef}
                src={meeting.recording_url}
                segments={meeting.streaming_segments}
                speakerColors={speakerColorMap}
                speakerNames={meeting.speaker_names}
                chapters={meeting.chapters}
                showChaptersWhenEmpty
                initialSeekSeconds={seekSeconds}
              />
            ) : null
          }
        />
      </SpeakerColorsProvider>
    )
  }

  return (
    <>
      <SpeakerColorsProvider colors={speakerColorMap}>
        <Container size="md">
          {!isActivelyRecording && (
            <>
              <Anchor component={Link} to="/" size="sm" mb="xs">
                &larr; <Trans>Back to meetings</Trans>
              </Anchor>

              <Group justify="space-between" mb="xs">
                <EditableField
                  value={displayMeetingTitle(meeting.subject, meeting.topics, i18n)}
                  onSave={(v) => updateSubject(company, id, v)}
                  component="title"
                  c="var(--mantine-color-text)"
                />
                <StatusPill phase={meeting.status} />
              </Group>

              <Group gap="md" mb="md">
                <Text size="sm" c="dimmed">
                  {meeting.organizer_name || meeting.organizer_email}
                </Text>
                {meeting.start_time && (
                  <Text size="sm" c="dimmed">
                    {formatDateTime(i18n, meeting.start_time)}
                  </Text>
                )}
              </Group>
            </>
          )}

          {!isActivelyRecording && (
            <Group mb="lg">
              <Button renderRoot={(props) => <Link to="/meetings/$id/transcript" params={{ id }} {...props} />}>
                <Trans>View transcript</Trans>
              </Button>
              {!isIntermediate && (
                <ReprocessForm
                  meetingId={id}
                  hasRecording={meeting?.source?.includes("browser") || meeting?.source?.includes("recording")}
                  templateId={meeting?.template_id}
                  meetingLanguage={meeting?.analysis_locale}
                  hasManualEdits={Object.keys(meeting?.field_edits ?? {}).length > 0}
                  onReprocessed={() => refetch()}
                />
              )}
              {!isIntermediate && companyId && <ExportMenu meetingId={id} companyId={companyId} />}
              {!isIntermediate && companyId && canManageAccess && (
                <ShareMeetingMenu meetingId={id} companyId={companyId} />
              )}
              {canManageAccess &&
                (() => {
                  const extraCount = sharedWithEmails.filter(
                    (e) => e.toLowerCase() !== meeting.organizer_email.toLowerCase(),
                  ).length
                  return (
                    <Button
                      variant="default"
                      onClick={openAccess}
                      rightSection={
                        extraCount > 0 ? <Badge size="xs" variant="filled" circle>{`+${extraCount}`}</Badge> : undefined
                      }
                    >
                      <Trans>Manage access</Trans>
                    </Button>
                  )
                })()}
              {isOrgAdmin && meeting.hidden && (
                <Button
                  variant="subtle"
                  color="success"
                  onClick={async () => {
                    await unhideMeeting(company, id)
                    queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, id) })
                    queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
                  }}
                >
                  <Trans>Unhide meeting</Trans>
                </Button>
              )}
              {(isOrgAdmin || isOrganizer) && !meeting.hidden && (
                <Button
                  variant="subtle"
                  color="danger"
                  onClick={() => {
                    modals.openConfirmModal({
                      title: i18n._(msg`Hide this meeting?`),
                      children: (
                        <Text size="sm">
                          <Trans>Hidden meetings are removed from everyone's list until an admin restores them.</Trans>
                        </Text>
                      ),
                      labels: { confirm: i18n._(msg`Hide`), cancel: i18n._(msg`Cancel`) },
                      onConfirm: () => {
                        hideMeeting(company, id)
                          .then(() => {
                            queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
                            notifications.show({ message: i18n._(msg`Meeting hidden`), color: "gray" })
                            navigateHome()
                          })
                          .catch((err) => {
                            notifyError(err, i18n._(msg`Action failed`))
                          })
                      },
                    })
                  }}
                >
                  Hide meeting
                </Button>
              )}
              {isOrgAdmin && (
                <Button
                  variant="subtle"
                  color="danger"
                  onClick={() => {
                    modals.openConfirmModal({
                      title: i18n._(msg`Delete meeting permanently?`),
                      children: (
                        <Text size="sm">
                          <Trans>
                            This action cannot be undone. The recording, transcript, and analysis are removed.
                          </Trans>
                        </Text>
                      ),
                      labels: { confirm: i18n._(msg`Delete`), cancel: i18n._(msg`Cancel`) },
                      confirmProps: { color: "danger" },
                      onConfirm: () => {
                        deleteMeetingPermanently(company, id)
                          .then(() => {
                            queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
                            notifications.show({ message: i18n._(msg`Meeting permanently deleted`), color: "danger" })
                            navigateHome()
                          })
                          .catch((err) => {
                            notifyError(err, i18n._(msg`Delete failed`))
                          })
                      },
                    })
                  }}
                >
                  Delete permanently
                </Button>
              )}
            </Group>
          )}

          <MeetingAccessModal opened={accessOpened} onClose={closeAccess} meetingId={id} onChanged={() => refetch()} />

          {!isActivelyRecording && !isObserver && meeting.analysis && (
            <Group justify="space-between" align="center" wrap="nowrap" mb="xs">
              <MeetingStats meeting={meeting as IMeetingDetail & { attendee_emails?: string }} />
              <MeetingLanguageSwitcher
                sourceLocale={sourceLocale}
                availableTranslations={availableTranslations}
                currentLocale={analysisLocale}
                isTranslating={isTranslating}
                onLocaleChange={onLocaleChange}
              />
            </Group>
          )}

          {hasTranslationError && (
            <Alert color="danger" variant="light" mb="md">
              <Trans>Translation failed. Showing the original analysis.</Trans>
            </Alert>
          )}

          {isActivelyRecording && (
            <RecordingControls
              meetingId={id}
              onRecordingStopped={() => refetch()}
              meetingSubject={meeting.subject}
              meetingStartTime={meeting.start_time ?? null}
            />
          )}

          {/* Observer live view — shown when watching an in-progress meeting from another device/browser */}
          {isObserver && (
            <Paper withBorder p="md" mb="md">
              <Group justify="space-between" mb="sm">
                <Text fw={600} size="sm">
                  Live Transcript
                </Text>
                <Badge color={liveData.isStreaming ? "success" : "gray"} variant="dot" size="sm">
                  {liveData.isStreaming ? "Live" : "Waiting…"}
                </Badge>
              </Group>

              {/* Live speaker-recognition — an interactive confirm/deny pill for a
                  viewer who can edit speakers (the organizer watching from another
                  device, or an admin), else a display-only recognition strip. */}
              <SpeakerHypothesisStrip
                hypotheses={[...liveData.speakerHypotheses.values()]}
                interactive={meeting.can_edit ?? false}
                pending={hypothesisPending}
                onAction={onHypothesisAction}
              />

              <ScrollArea h={300} mb="sm">
                <Stack gap={4}>
                  {liveData.segments.map((seg) => (
                    <Text key={`${seg.start}-${seg.speaker}`} size="sm">
                      <Text component="span" fw={600} c="dimmed" size="xs">
                        {seg.speaker}:{" "}
                      </Text>
                      {seg.text}
                    </Text>
                  ))}
                  {liveData.interimText && (
                    <Text size="sm" c="dimmed" fs="italic">
                      <Text component="span" fw={600} size="xs">
                        {liveData.interimSpeaker}:{" "}
                      </Text>
                      {liveData.interimText}
                    </Text>
                  )}
                  {liveData.segments.length === 0 && !liveData.interimText && (
                    <Text size="sm" c="dimmed">
                      Waiting for transcript…
                    </Text>
                  )}
                </Stack>
              </ScrollArea>

              {liveData.latestSummary && (
                <Box>
                  <Text fw={600} size="sm" mb="xs">
                    Current Discussion
                  </Text>
                  <Text size="sm" mb="xs">
                    {liveData.latestSummary.currentDiscussion}
                  </Text>
                  {liveData.latestSummary.keyPoints.length > 0 && (
                    <Stack gap={2}>
                      {liveData.latestSummary.keyPoints.map((kp) => (
                        <Text key={kp} size="xs" c="dimmed">
                          · {kp}
                        </Text>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
            </Paper>
          )}

          {meeting.recording_url && !isIntermediate && (
            <Player
              ref={audioPlayerRef}
              src={meeting.recording_url}
              segments={meeting.streaming_segments}
              speakerColors={speakerColorMap}
              speakerNames={meeting.speaker_names}
              chapters={meeting.chapters}
              showChaptersWhenEmpty
              initialSeekSeconds={seekSeconds}
            />
          )}

          {speakerVMs.speakers.length > 0 && (
            <SpeakerPanel
              meetingId={id}
              speakers={speakerVMs.speakers}
              analysis={meeting.analysis as IMeetingDetail["analysis"]}
              onRenamed={() => refetch()}
              segments={meeting.streaming_segments}
              audioUrl={meeting.recording_url}
              // Wire the card "listen" dot to the one mounted player above; omit
              // it when no player is shown so the dot never renders as a dead cue.
              onListen={
                meeting.recording_url && !isIntermediate
                  ? (seconds) => audioPlayerRef.current?.seekTo(seconds)
                  : undefined
              }
            />
          )}

          {/* Finalization stepper — shown while processing, not during active recording */}
          {isIntermediate && !isActivelyRecording && (
            <FinalizationProgress
              stage={effectiveStage}
              progress={effectiveProgress}
              currentStepName={currentStepName}
            />
          )}

          {/* Live progress note during active recording */}
          {isIntermediate && isActivelyRecording && meeting.analysis && (
            <Alert color="info" variant="light" mb="md">
              Meeting in progress — analysis updates automatically
            </Alert>
          )}

          {meeting.analysis && displayAnalysis ? (
            <Box
              opacity={isIntermediate ? 0.7 : 1}
              className={!isIntermediate ? styles.analysisReveal : undefined}
              style={{ transition: "opacity 0.4s ease" }}
            >
              {/* display_sections is always populated by the backend (resolved
                  from the template), so TemplateAnalysisView is the only path —
                  the guard stays purely defensive against a missing value. */}
              {meeting.display_sections && (
                <TemplateAnalysisView
                  analysis={normalizeAnalysis(displayAnalysis, speakerLabelDisplayMap(speakerVMs.speakers))}
                  sections={meeting.display_sections as IDisplaySection[]}
                  editContext={
                    analysisLocale === sourceLocale
                      ? ({
                          canEdit: meeting.can_edit ?? false,
                          fieldEdits: meeting.field_edits ?? {},
                          meetingId: id,
                          companyId,
                          onEdited: () => refetch(),
                        } satisfies IEditContext)
                      : undefined
                  }
                />
              )}
            </Box>
          ) : !isIntermediate ? (
            <Text c="dimmed" ta="center" py="xl">
              <Trans>No analysis available yet.</Trans>
            </Text>
          ) : null}
          {/* Post-session TTRPG artifacts — shown when session has a series and is not live */}
          {meeting.series_id && !isIntermediate && myRole && myRole !== "observer" && (
            <TTRPGReports meetingId={id} seriesId={meeting.series_id} isDM={myRole === "dm"} />
          )}
          {/* Canon review — DM/players triage proposed journal + chronicle entries */}
          {meeting.series_id && !isIntermediate && myRole && myRole !== "observer" && (
            <Box mt="xl">
              <CanonReviewPane seriesId={meeting.series_id} meetingId={id} />
            </Box>
          )}
        </Container>
      </SpeakerColorsProvider>
      <TTRPGPanel
        meetingId={id}
        seriesId={meeting.series_id}
        isLive={meeting.status === MEETING_STATUSES.IN_PROGRESS}
      />
    </>
  )
}
